import { ReactElement, useState, useEffect } from 'react';
import { CabModal, CabImageUpload } from '@CabComponents';
import { CabButton } from '@CabComponents/CabButton';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { Leader, OrganizationLeader, ThunkDispatchType, actions } from '../../../../store';
import { FixedNoteField } from '../../../../components/InfoCard/EditNote/EditNote';
import { ERROR_TYPE, EVENT_TYPE } from '../../../../constants';
import { trackEvent } from '../../../../utils/appAnalyticsUtils';
import { validateEmail, addFieldError, removeFieldError, FieldErrors } from '../../../../utils/inputUtils';

interface ComponentProps {
  leader: OrganizationLeader;
  isOpen: boolean;
  onDidDismiss: () => void;
}


type FieldKey = 'first_name'|'last_name'|'company'|'role'|'email'|'phone_number';

type Props = ComponentProps;

export const AdminEditLeader = 
  ({ leader, isOpen, onDidDismiss}: Props): ReactElement => {

    const dispatch = useDispatch<ThunkDispatchType>();
    const updateLeader = (
      leaderUpdate: Partial<Leader> & { id: number }, 
      leaderPic: File | null, 
      removeLeaderPic?: boolean,
    ) => dispatch(actions.leaders.updateAdminLeader(leaderUpdate, leaderPic, removeLeaderPic));

    const [leaderPic, setLeaderPic] = useState<File | null>(null);

    const [editedLeader, setEditedLeader] = useState<OrganizationLeader>(leader);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [formError, setFormError] = useState<string | null>(null);

    useEffect((): void => {
      if (isOpen) {
        setEditedLeader(leader);
        setFieldErrors({ });
      }
    }, [leader, isOpen]);

    const handleEmailChange = (email: string): void => {
      if (validateEmail(email)) {
        handleRemoveError('email', ERROR_TYPE.EMAIL);
      } else {
        handleAddError('email', ERROR_TYPE.EMAIL);
      }
    };

    const validateRequiredFieldChange = (fieldKey: FieldKey): ((newValue: string) => void) => {
      return (newValue: string): void => {
        if (newValue === '') {
          handleAddError(fieldKey, ERROR_TYPE.REQUIRED);
        } else {
          handleRemoveError(fieldKey, ERROR_TYPE.REQUIRED);
        }
      };
    };

    const changeFunc = (key: keyof OrganizationLeader) => (value: string): void => {
      setEditedLeader((prev) => ({...prev, [key]: value}));
    };

    const handleAddError = (fieldKey: FieldKey, errorType: keyof typeof ERROR_TYPE): void => {
      setFieldErrors(addFieldError(fieldErrors, fieldKey, errorType));
    };
  
    const handleRemoveError = (fieldKey: FieldKey, errorType: keyof typeof ERROR_TYPE): void => {
      setFieldErrors(removeFieldError(fieldErrors, fieldKey, errorType));
    };

    const getErrors = () => {
      const fieldMessages: string[] = [];
      Object.values(fieldErrors).forEach(field => {
        Object.values(field).forEach((error): void => {
          if (!error.valid) {
            fieldMessages.push(error.message);
          }
        });
      });

      return fieldMessages.join('');
    };

    const handleCancel = (): void => {
      onDidDismiss();
    };

    const handleSave = (): void => {
      const errors = getErrors();
      if (!errors) {
        const tempLeader: Partial<Leader> & {id: number} = {
          ...editedLeader,
          primary_assistant: editedLeader.primary_assistant?.id,
        };
        // If there was a photo originally but now no photo is selected, mark as removed
        const removedPic = !!(leader.pic_url && !leaderPic);
        updateLeader(tempLeader, leaderPic, removedPic).then((): void => {
          setLeaderPic(null);
          dispatch(actions.leaders.fetchLeaders());
          onDidDismiss();
        });
        trackEvent(EVENT_TYPE.UPDATE_EXEC);
      } else {
        setFormError(errors);
      }
    };

    return (
      <>
        <CabModal
          open={isOpen}
          onClose={handleCancel}
          closeIcon
          closeOnBackdropClick
          title={`Edit ${leader.first_name} ${leader.last_name}`}
          actionButtons={
            <CabButton color='primary' onClick={handleSave}>
              Save
            </CabButton>
          }
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column'
            }}>
            <CabImageUpload
              currentImage={leader.pic_url}
              avatarName={`${leader.first_name} ${leader.last_name}`}
              displayMode="avatar"
              avatarSize="large"
              avatarColor={leader.color}
              onImageSelect={(file) => {
                setLeaderPic(file);
              }}
              useDropzone={true}
              dragDropText="Drag & drop image or"
              clickText="click"
              fileTypeText="JPG or PNG. Max"
              maxSizeText="2.5Mb"
              sx={{ marginTop: 2, marginBottom: 2 }}
              showRemoveButton
              onImageRemove={() => setLeaderPic(null)}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}>
              <FixedNoteField fieldLabelKey="first_name" placeholder="Enter a name" 
                value={editedLeader.first_name} changeFunc={changeFunc('first_name')}
                validateField={validateRequiredFieldChange('first_name')}/>

              <FixedNoteField fieldLabelKey="last_name" placeholder="Enter a name" 
                value={editedLeader.last_name} changeFunc={changeFunc('last_name')} 
                validateField={validateRequiredFieldChange('last_name')}/>

              <FixedNoteField fieldLabelKey="company" placeholder="Company" 
                value={editedLeader.company} changeFunc={changeFunc('company')} />

              <FixedNoteField fieldLabelKey="role" placeholder="Job title" 
                value={editedLeader.role} changeFunc={changeFunc('role')} />

              <FixedNoteField fieldLabelKey="email" placeholder="Email" 
                value={editedLeader.email} changeFunc={changeFunc('email')} 
                validateField={handleEmailChange}/>

              <FixedNoteField fieldLabelKey="phone_number" placeholder="Phone number" 
                value={editedLeader.phone_number} changeFunc={changeFunc('phone_number')}/>
            </Box>
          </Box>
        </CabModal>
        <CabModal
          open={!!formError}
          isAlert
          closeOnBackdropClick
          noFullScreen={true}
          onClose={(): void => setFormError(null)}
          title={'Errors'}
          text={formError || ''}
          actionButtons={
            <CabButton buttonType='primary' color='primary' onClick={(): void => setFormError(null)}>
              Okay
            </CabButton>
          }
        />
      </>
    );
  };

export default AdminEditLeader;